import { useCallback, useEffect, useRef, useState } from "react";
import { TONE_DOT, TONE_SOFT } from "./tones";

export interface KanbanColumn {
  key: string;
  label: string;
  isTerminal?: boolean;
  /** Overrides STATUS_DOT_COLORS for boards whose status keys don't belong
   * to the shared job-pipeline vocabulary (e.g. CRM stage keys). */
  dotColorClass?: string;
}

// Covers every status value used across the four job pipelines (the shared
// 9-value workflow plus Upwork's own 14-value one) — one palette, reused by
// every board so a given status reads the same color everywhere.
export const STATUS_DOT_COLORS: Record<string, string> = {
  new: TONE_DOT.neutral,
  analyzing: TONE_DOT.info,
  recommended: TONE_DOT.brand,
  reviewing: TONE_DOT.info,
  assigned: TONE_DOT.brand,
  employee_checking: TONE_DOT.brand,
  eligible: TONE_DOT.success,
  not_eligible: TONE_DOT.danger,
  proposal_prepared: TONE_DOT.teal,
  applied: TONE_DOT.teal,
  applied_manually: TONE_DOT.teal,
  interviewing: TONE_DOT.warning,
  offer_received: TONE_DOT.success,
  hired: TONE_DOT.success,
  rejected: TONE_DOT.danger,
  skipped: TONE_DOT.neutral,
  expired: TONE_DOT.neutral,
  not_relevant: TONE_DOT.neutral,
  archived: TONE_DOT.neutral,
};

// Default page size per column — the board fetches one status's worth of
// cards at a time, not the whole pipeline, and loads more only as each
// column is scrolled toward its own bottom.
export const COLUMN_PAGE_SIZE = 20;

interface ColumnState<T> {
  items: T[];
  total: number;
  page: number;
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
}

function emptyColumnState<T>(): ColumnState<T> {
  return { items: [], total: 0, page: 0, loading: false, loadingMore: false, error: false };
}

interface Props<T> {
  columns: KanbanColumn[];
  getId: (item: T) => number;
  getStatus: (item: T) => string;
  withStatus: (item: T, newStatus: string) => T;
  /** Fetches one page of items for a single column (status). Called with
   * page=1 on initial load/version bump and page=N+1 as that column is
   * scrolled toward its bottom. */
  fetchColumnItems: (statusKey: string, page: number, pageSize: number) => Promise<{ items: T[]; total: number }>;
  onStatusChange: (item: T, newStatus: string) => Promise<void>;
  renderCard: (item: T) => React.ReactNode;
  /** Bumped by the parent when something outside the board (e.g. a detail
      modal) may have changed items — triggers a reload of every column. */
  version?: number;
  pageSize?: number;
}

export default function StatusKanbanBoard<T>({
  columns,
  getId,
  getStatus,
  withStatus,
  fetchColumnItems,
  onStatusChange,
  renderCard,
  version = 0,
  pageSize = COLUMN_PAGE_SIZE,
}: Props<T>) {
  const [colState, setColState] = useState<Record<string, ColumnState<T>>>({});
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const draggedId = useRef<number | null>(null);
  // Guards against a stale response landing after a newer reload/version
  // bump for the same column has already started.
  const reqSeq = useRef<Record<string, number>>({});

  const loadColumnPage = useCallback(async (columnKey: string, page: number) => {
    const reqId = (reqSeq.current[columnKey] ?? 0) + 1;
    reqSeq.current[columnKey] = reqId;
    setColState((prev) => ({
      ...prev,
      [columnKey]: {
        ...(prev[columnKey] ?? emptyColumnState<T>()),
        loading: page === 1,
        loadingMore: page > 1,
        error: false,
      },
    }));
    try {
      const res = await fetchColumnItems(columnKey, page, pageSize);
      if (reqSeq.current[columnKey] !== reqId) return;
      setColState((prev) => {
        const existing = prev[columnKey] ?? emptyColumnState<T>();
        return {
          ...prev,
          [columnKey]: {
            items: page === 1 ? res.items : [...existing.items, ...res.items],
            total: res.total,
            page,
            loading: false,
            loadingMore: false,
            error: false,
          },
        };
      });
    } catch {
      if (reqSeq.current[columnKey] !== reqId) return;
      setColState((prev) => ({
        ...prev,
        [columnKey]: { ...(prev[columnKey] ?? emptyColumnState<T>()), loading: false, loadingMore: false, error: true },
      }));
    }
  }, [fetchColumnItems, pageSize]);

  const loadAllColumns = useCallback(() => {
    columns.forEach((col) => { loadColumnPage(col.key, 1); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadColumnPage, columns.map((c) => c.key).join(",")]);

  useEffect(() => { loadAllColumns(); }, [loadAllColumns, version]);

  function handleColumnScroll(columnKey: string, el: HTMLDivElement) {
    const state = colState[columnKey];
    if (!state || state.loading || state.loadingMore) return;
    if (state.items.length >= state.total) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120;
    if (nearBottom) loadColumnPage(columnKey, state.page + 1);
  }

  async function handleDrop(columnKey: string) {
    setDragOverKey(null);
    const id = draggedId.current;
    draggedId.current = null;
    if (id === null) return;

    let sourceKey: string | null = null;
    let draggedItem: T | null = null;
    for (const col of columns) {
      const found = (colState[col.key]?.items ?? []).find((i) => getId(i) === id);
      if (found) { sourceKey = col.key; draggedItem = found; break; }
    }
    if (!draggedItem || sourceKey === null || sourceKey === columnKey) return;

    const movedItem = withStatus(draggedItem, columnKey);
    // Optimistic move for a snappy drop — pull the card out of its source
    // column and drop it at the top of the target column immediately.
    setColState((prev) => {
      const source = prev[sourceKey!];
      const target = prev[columnKey] ?? emptyColumnState<T>();
      return {
        ...prev,
        [sourceKey!]: { ...source, items: source.items.filter((i) => getId(i) !== id), total: Math.max(0, source.total - 1) },
        [columnKey]: { ...target, items: [movedItem, ...target.items], total: target.total + 1 },
      };
    });

    let failed = false;
    try {
      await onStatusChange(draggedItem, columnKey);
    } catch {
      failed = true;
    }
    // Reconcile both affected columns from page 1 — simplest way to
    // guarantee correctness after a move; any additional pages the user
    // had scrolled into for those two columns reset, which is an
    // acceptable tradeoff for a drag-and-drop edge case.
    await Promise.all([loadColumnPage(sourceKey, 1), loadColumnPage(columnKey, 1)]);
    if (failed) {
      setColState((prev) => ({ ...prev, [columnKey]: { ...prev[columnKey], error: true } }));
    }
  }

  const anyError = columns.some((col) => colState[col.key]?.error);

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-2">
      {anyError && (
        <p className={`text-xs border rounded-lg px-3 py-2 ${TONE_SOFT.danger}`}>
          Failed to load one or more columns — try again.
        </p>
      )}
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-3 h-full min-h-[420px] pb-2">
          {columns.map((col) => {
            const state = colState[col.key] ?? emptyColumnState<T>();
            const isOver = dragOverKey === col.key;
            return (
              <div
                key={col.key}
                onDragOver={(e) => { e.preventDefault(); setDragOverKey(col.key); }}
                onDragLeave={() => setDragOverKey((k) => (k === col.key ? null : k))}
                onDrop={() => handleDrop(col.key)}
                className={`w-72 shrink-0 flex flex-col rounded-xl border transition-colors ${
                  isOver ? "border-primary bg-primary-soft/60" : "border-border bg-border/50"
                } ${col.isTerminal ? "opacity-80" : ""}`}
              >
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${col.dotColorClass ?? STATUS_DOT_COLORS[col.key] ?? TONE_DOT.neutral}`} />
                  <span className="text-xs font-medium text-foreground font-inter truncate">{col.label}</span>
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                    {state.total}
                  </span>
                </div>
                <div
                  className="flex-1 overflow-y-auto p-2 space-y-2"
                  onScroll={(e) => handleColumnScroll(col.key, e.currentTarget)}
                >
                  {state.loading && state.items.length === 0 && (
                    <div className="h-16 rounded-xl bg-surface-muted/40 animate-pulse" />
                  )}
                  {state.items.map((item) => {
                    const id = getId(item);
                    return (
                      <div
                        key={id}
                        draggable
                        onDragStart={() => { draggedId.current = id; }}
                        onDragEnd={() => { draggedId.current = null; setDragOverKey(null); }}
                        className="cursor-grab active:cursor-grabbing"
                      >
                        {renderCard(item)}
                      </div>
                    );
                  })}
                  {state.loadingMore && (
                    <div className="h-14 rounded-xl bg-surface-muted/40 animate-pulse" />
                  )}
                  {!state.loading && state.items.length === 0 && (
                    <p className="text-[11px] text-muted-foreground font-inter text-center py-4 select-none">
                      {isOver ? "Drop here" : "—"}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
